import { useEffect, useRef } from "react";

export type Vehicle = {
  vehicle_id: string;
  route_id?: string;
  route_short_name?: string;
  /** Route color as a hex string without the leading '#', e.g. "28813F". */
  route_color?: string;
  trip_id?: string;
  trip_headsign?: string;
  lat: number;
  lon: number;
  bearing?: number;
  speed?: number;
  /** Phase reported by the source (e.g. "in_progress"). */
  status?: string;
  /** Unix seconds of last position update. */
  ts: number;
};

export type Snapshot = {
  vehicles: Vehicle[];
  generated: number;
};

type Options = {
  url?: string;
  onSnapshot: (snap: Snapshot) => void;
  onStatusChange?: (connected: boolean) => void;
};

// useVehicleStream subscribes to the SSE feed of vehicle snapshots. The
// browser's EventSource handles automatic reconnection for us.
//
// Callbacks are stashed in a ref so callers can pass inline closures (which
// would otherwise be a fresh function identity on every render) without
// causing the effect to tear the EventSource down and re-open it on every
// parent re-render — that pattern produces a flood of /api/realtime/vehicles
// requests to the backend.
export function useVehicleStream({
  url = "/api/realtime/vehicles",
  onSnapshot,
  onStatusChange,
}: Options) {
  const handlersRef = useRef({ onSnapshot, onStatusChange });
  handlersRef.current = { onSnapshot, onStatusChange };

  useEffect(() => {
    const es = new EventSource(url);
    es.addEventListener("open", () =>
      handlersRef.current.onStatusChange?.(true),
    );
    es.addEventListener("error", () =>
      handlersRef.current.onStatusChange?.(false),
    );
    es.addEventListener("vehicles", (ev) => {
      try {
        const snap = JSON.parse((ev as MessageEvent).data) as Snapshot;
        handlersRef.current.onSnapshot(snap);
      } catch {
        // Ignore malformed payloads; next snapshot will arrive shortly.
      }
    });
    return () => es.close();
  }, [url]);
}
